import { useEffect, useState } from "react";
import {
    ArrowDown, DownloadSimple, Receipt, Stack, Coins,
    ArrowCounterClockwise, FunnelSimple, Buildings,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { formatINR } from "../../lib/format";
import PageHeader from "../../components/layout/PageHeader";
import EmptyState from "../../components/EmptyState";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import { cn as classJoin } from "../../lib/utils";

function currentFy() {
    const now = new Date();
    const y = now.getFullYear();
    const start = now.getMonth() >= 3 ? y : y - 1;
    return `${start}-${String((start + 1) % 100).padStart(2, "0")}`;
}

function fyOptions() {
    const cur = currentFy();
    const startYear = parseInt(cur.split("-")[0], 10);
    return Array.from({ length: 6 }, (_, i) => {
        const s = startYear - i + 1;
        return { value: `${s - 1}-${String(s % 100).padStart(2, "0")}`, label: `FY ${s - 1}-${String(s % 100).padStart(2, "0")}` };
    });
}

export default function VendorGstCredits() {
    const [fy, setFy] = useState(currentFy());
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        try {
            const r = await api.get("/billing/reports/vendor-compliance/gst", { params: { fy } });
            setData(r.data);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load Vendor GST Credits"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [fy]);

    const csvVendor = () => {
        const rows = data?.input?.by_vendor || [];
        if (!rows.length) return;
        const cols = ["Vendor Code", "Vendor", "GSTIN", "Transactions",
            "Taxable Value", "GST %", "GST Credit", "Invoice Total"];
        const body = rows.map((r) => [
            r.vendor_code, r.vendor_name, r.vendor_gstin || "",
            r.txn_count, r.taxable_value, r.effective_gst_percent,
            r.gst_amount, r.invoice_total,
        ].map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","));
        download(`vendor-gst-credits-by-vendor-${fy}.csv`, [cols.join(","), ...body].join("\n"));
    };

    const csvInvoice = () => {
        const rows = data?.input?.items || [];
        if (!rows.length) return;
        const cols = ["Source", "Code", "Invoice/Bill Number", "Date", "Vendor Code",
            "Vendor", "GSTIN", "Taxable Value", "GST %", "GST Amount", "Invoice Total"];
        const body = rows.map((r) => [
            r.source_label, r.code, r.txn_number, r.txn_date, r.vendor_code,
            r.vendor_name, r.vendor_gstin || "", r.taxable_value,
            r.gst_percent, r.gst_amount, r.invoice_total,
        ].map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","));
        download(`vendor-gst-credits-detail-${fy}.csv`, [cols.join(","), ...body].join("\n"));
    };

    if (loading && !data) {
        return (
            <div data-testid="vendor-gst-credits-page-loading">
                <PageHeader eyebrow="Compliance · Vendor GST Credits" title="Loading…" />
            </div>
        );
    }
    if (!data) return null;

    const s = data.summary || {};
    const byVendor = data.input?.by_vendor || [];
    const items = data.input?.items || [];

    return (
        <div data-testid="vendor-gst-credits-page">
            <PageHeader
                eyebrow={`Compliance · Vendor GST Credits · FY ${fy}`}
                title={
                    <span className="flex items-center gap-3">
                        <ArrowDown size={22} weight="duotone" className="text-indigo-600" />
                        Vendor GST Credits
                    </span>
                }
                description="Input Tax Credit available from vendor bills and service invoices. Feeds the Net GST Liability calculation."
            />

            <div className="px-6 sm:px-8 py-8 space-y-6">
                {/* Summary cards */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-3" data-testid="vendor-gst-credits-summary">
                    <Kpi label="Total Input GST" value={formatINR(s.input_gst || 0)}
                         sub="Available as Input Tax Credit"
                         icon={<Coins size={11} weight="fill" />} tone="indigo" testid="kpi-total-input-gst" />
                    <Kpi label="Total Taxable Value" value={formatINR(s.input_taxable || 0)}
                         sub="Pre-GST value of vendor transactions"
                         icon={<Receipt size={11} weight="fill" />} tone="slate" testid="kpi-total-taxable" />
                    <Kpi label="Total Transactions" value={String(s.input_count || 0)}
                         sub={`Across ${data.input?.vendor_count || 0} vendor${(data.input?.vendor_count || 0) === 1 ? "" : "s"}`}
                         icon={<Stack size={11} weight="fill" />} tone="emerald" testid="kpi-total-txns" />
                </div>

                {/* Filter bar */}
                <div className="reedos-card p-4" data-testid="vendor-gst-credits-filters">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
                        <FilterSelect label="Financial Year" value={fy} onChange={setFy} options={fyOptions()} testid="filter-fy" />
                        <div className="sm:col-span-2 flex items-center gap-2 justify-end">
                            <div className="text-[11px] text-slate-500 mr-2">
                                FY window: <span className="font-mono">{data.window?.from} → {data.window?.to}</span>
                            </div>
                            <Button variant="outline" size="sm" onClick={() => setFy(currentFy())} className="gap-1.5" data-testid="filter-reset-btn">
                                <ArrowCounterClockwise size={12} /> Current FY
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Vendor-wise rollup */}
                <div className="reedos-card overflow-hidden" data-testid="vendor-gst-rollup">
                    <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
                        <Buildings size={14} className="text-slate-500" />
                        <div className="font-display font-bold text-sm">Vendor-wise GST Credits</div>
                        <div className="text-[11px] text-slate-500 ml-2">
                            {byVendor.length} vendor{byVendor.length === 1 ? "" : "s"} · sorted by GST credit (desc)
                        </div>
                        <div className="ml-auto">
                            <Button variant="outline" size="sm" onClick={csvVendor} disabled={!byVendor.length} className="gap-1.5" data-testid="export-vendor-csv-btn">
                                <DownloadSimple size={13} /> Export Vendor Summary CSV
                            </Button>
                        </div>
                    </div>
                    {byVendor.length === 0 ? (
                        <EmptyState title="No GST credits available" description="No vendor bills or service invoices with GST were recorded for this FY." />
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-slate-50 border-b border-slate-200">
                                    <tr className="text-left">
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px]">Vendor</th>
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px]">GSTIN</th>
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px] text-right">Transactions</th>
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px] text-right">Taxable Value</th>
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px] text-right">GST %</th>
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px] text-right">GST Credit</th>
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px] text-right">Invoice Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {byVendor.map((v) => (
                                        <tr key={v.vendor_id || "u"} className="border-t border-slate-100 reedos-row-hover" data-testid={`vendor-gst-rollup-row-${v.vendor_id || "u"}`}>
                                            <td className="px-4 py-2.5 text-xs">
                                                <div className="font-semibold text-slate-900">{v.vendor_name || "—"}</div>
                                                <div className="font-mono text-[10px] text-slate-500">{v.vendor_code}</div>
                                            </td>
                                            <td className="px-4 py-2.5 text-xs font-mono text-slate-700">
                                                {v.vendor_gstin || <span className="text-slate-300">—</span>}
                                            </td>
                                            <td className="px-4 py-2.5 text-xs text-right reedos-data text-slate-600">{v.txn_count}</td>
                                            <td className="px-4 py-2.5 text-xs text-right reedos-data text-slate-700">{formatINR(v.taxable_value)}</td>
                                            <td className="px-4 py-2.5 text-xs text-right reedos-data text-slate-600" title="GST Amount × 100 ÷ Taxable Value">
                                                {(v.effective_gst_percent || 0).toFixed(2)}%
                                            </td>
                                            <td className="px-4 py-2.5 text-xs text-right reedos-data font-bold text-indigo-700">{formatINR(v.gst_amount)}</td>
                                            <td className="px-4 py-2.5 text-xs text-right reedos-data text-slate-700">{formatINR(v.invoice_total || 0)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot className="bg-slate-50 border-t border-slate-200">
                                    <tr className="font-semibold">
                                        <td colSpan={2} className="px-4 py-3 text-right reedos-eyebrow">Total</td>
                                        <td className="px-4 py-3 text-right reedos-data text-slate-600">{s.input_count || 0}</td>
                                        <td className="px-4 py-3 text-right reedos-data text-slate-700">{formatINR(s.input_taxable || 0)}</td>
                                        <td className="px-4 py-3 text-right reedos-data text-slate-500">—</td>
                                        <td className="px-4 py-3 text-right reedos-data text-indigo-700 font-bold">{formatINR(s.input_gst || 0)}</td>
                                        <td className="px-4 py-3 text-right reedos-data text-slate-700">{formatINR(s.input_invoice_total || 0)}</td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    )}
                </div>

                {/* Invoice-wise detail */}
                <div className="reedos-card overflow-hidden" data-testid="vendor-gst-detail">
                    <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
                        <FunnelSimple size={14} className="text-slate-500" />
                        <div className="font-display font-bold text-sm">Invoice-wise Detail</div>
                        <div className="text-[11px] text-slate-500 ml-2">
                            {items.length} transaction{items.length === 1 ? "" : "s"} · Vendor Bills + Service Invoices
                        </div>
                        <div className="ml-auto">
                            <Button variant="outline" size="sm" onClick={csvInvoice} disabled={!items.length} className="gap-1.5" data-testid="export-invoice-csv-btn">
                                <DownloadSimple size={13} /> Export Invoice Detail CSV
                            </Button>
                        </div>
                    </div>
                    {items.length === 0 ? (
                        <EmptyState title="No GST transactions" description="No taxable vendor transactions found for this FY." />
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-slate-50 border-b border-slate-200">
                                    <tr className="text-left">
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px]">Source</th>
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px]">Code</th>
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px]">Invoice/Bill #</th>
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px]">Date</th>
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px]">Vendor</th>
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px]">GSTIN</th>
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px] text-right">Taxable Value</th>
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px] text-right">GST %</th>
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px] text-right">GST Amount</th>
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px] text-right">Invoice Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((r) => {
                                        // Surface mismatches between expected and effective GST
                                        // (e.g. operator override of gst_amount on service invoices).
                                        const computed = r.taxable_value > 0
                                            ? +(r.gst_amount * 100 / r.taxable_value).toFixed(2)
                                            : 0;
                                        const mismatch = Math.abs(computed - (r.gst_percent || 0)) > 0.05
                                            && (r.gst_percent || 0) > 0;
                                        return (
                                            <tr key={`${r.source}-${r.id}`} className="border-t border-slate-100 reedos-row-hover" data-testid={`vendor-gst-detail-row-${r.id}`}>
                                                <td className="px-4 py-2.5 text-xs">
                                                    <span className={classJoin("inline-block px-1.5 py-0.5 rounded font-semibold text-[10px]",
                                                        r.source === "vendor_bill" ? "bg-indigo-50 text-indigo-700 border border-indigo-200" :
                                                            "bg-violet-50 text-violet-700 border border-violet-200")}>
                                                        {r.source_label}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-2.5 text-xs font-mono font-semibold text-slate-900">{r.code}</td>
                                                <td className="px-4 py-2.5 text-xs font-mono text-slate-700">{r.txn_number}</td>
                                                <td className="px-4 py-2.5 text-xs text-slate-700">{r.txn_date}</td>
                                                <td className="px-4 py-2.5 text-xs">
                                                    <div className="font-semibold text-slate-900 truncate max-w-[180px]">{r.vendor_name || "—"}</div>
                                                    <div className="font-mono text-[10px] text-slate-500">{r.vendor_code}</div>
                                                </td>
                                                <td className="px-4 py-2.5 text-xs font-mono text-slate-700">
                                                    {r.vendor_gstin || <span className="text-slate-300">—</span>}
                                                </td>
                                                <td className="px-4 py-2.5 text-xs text-right reedos-data text-slate-700">{formatINR(r.taxable_value)}</td>
                                                <td className="px-4 py-2.5 text-xs text-right reedos-data text-slate-600"
                                                    title={mismatch
                                                        ? `Stored ${r.gst_percent}% but the GST amount works out to ${computed}% of the taxable value — likely an operator override of GST amount on the source invoice.`
                                                        : "GST Amount × 100 ÷ Taxable Value"}>
                                                    {(r.gst_percent || 0).toFixed(2)}%
                                                    {mismatch && (
                                                        <span className="ml-1 text-amber-600 font-bold" data-testid={`gst-mismatch-${r.id}`}>⚠</span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-2.5 text-xs text-right reedos-data font-bold text-indigo-700">{formatINR(r.gst_amount)}</td>
                                                <td className="px-4 py-2.5 text-xs text-right reedos-data text-slate-700">{formatINR(r.invoice_total || 0)}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                                <tfoot className="bg-slate-50 border-t border-slate-200">
                                    <tr className="font-semibold">
                                        <td colSpan={6} className="px-4 py-3 text-right reedos-eyebrow">Total</td>
                                        <td className="px-4 py-3 text-right reedos-data text-slate-700">{formatINR(s.input_taxable || 0)}</td>
                                        <td className="px-4 py-3 text-right reedos-data text-slate-500">—</td>
                                        <td className="px-4 py-3 text-right reedos-data text-indigo-700 font-bold">{formatINR(s.input_gst || 0)}</td>
                                        <td className="px-4 py-3 text-right reedos-data text-slate-700">{formatINR(s.input_invoice_total || 0)}</td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function download(filename, contents) {
    const blob = new Blob([contents], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

function Kpi({ label, value, sub, icon, tone = "slate", testid }) {
    const TONE = {
        slate: "text-slate-900",
        emerald: "text-emerald-700",
        rose: "text-rose-700",
        amber: "text-amber-700",
        indigo: "text-indigo-700",
        violet: "text-violet-700",
    };
    return (
        <div className="reedos-card p-4" data-testid={testid}>
            <div className="reedos-eyebrow flex items-center gap-1.5">{icon} {label}</div>
            <div className={classJoin("font-display font-bold reedos-data text-2xl mt-1 leading-none", TONE[tone])}>{value}</div>
            {sub && <div className="text-[11px] text-slate-500 mt-1 leading-tight">{sub}</div>}
        </div>
    );
}

function FilterSelect({ label, value, onChange, options, testid }) {
    return (
        <div>
            <Label className="text-[10px] font-semibold text-slate-500 uppercase">{label}</Label>
            <select value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full h-8 px-2 text-xs border border-slate-200 rounded bg-white" data-testid={testid}>
                {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
        </div>
    );
}
